import { DATA_FIELD_TYPE, BUILT_IN_FIELD_TYPE } from "Types/dataFieldTypes.js";
import { EXPRESSION_TYPES } from "Formula/expressionTypes.js";

const WIDGET_TYPE = { graph: 1, none: 2, table: 3, addWidget: 4 };

const CONTENT_TYPE = { DataField: 1, DataFieldGroup: 2, BuiltInField: 3 };

const WIDGET_MIN_DIMENSIONS = { h: 4, w: 4 };

const DIRECTIONAL_AXIS = { horizontal: 1, vertical: 2, diagonal: 3, none: 4 };

const FILTER_OPERATOR = {
    equals: 1,
    isNotEqual: 2,
    greaterThan: 3,
    lessThan: 4,
    isEmpty: 5,
    isNotEmpty: 6,
    greaterThanEqual: 7,
    lessThanEqual: 8
};

// Figure out a better way to make this number more reactive
const PIXELS_TO_REM = parseFloat(getComputedStyle(document.documentElement).fontSize);

const EDIT_WIDGET_MODAL_RESULT = { cancel: "cancel", save: "save" };
const DASHBOARD_RESULT = { cancel: "cancel", ignore: "ignore", save: "save" };

export {
    WIDGET_TYPE,
    CONTENT_TYPE,
    WIDGET_MIN_DIMENSIONS,
    DIRECTIONAL_AXIS,
    PIXELS_TO_REM,
    FILTER_OPERATOR,
    EDIT_WIDGET_MODAL_RESULT,
    DASHBOARD_RESULT
};

const EDIT_GRAPH_CATEGORY_FUNCTIONS = {
    Category: function (content) {
        const isNumberType = content.TypeId == DATA_FIELD_TYPE.Integer || content.TypeId == DATA_FIELD_TYPE.Decimal;
        const isBuiltInField = content.ContentType == CONTENT_TYPE.BuiltInField;
        // At the moment don't allow Built In Int values at the moment as they require more special handling
        return !(isNumberType && isBuiltInField);
    },
    Quantity: function (content) {
        return content.TypeId == DATA_FIELD_TYPE.Integer || content.TypeId == DATA_FIELD_TYPE.Decimal;
    }
};

const GRAPH_TYPE = { bar: 1, line: 2, pie: 3, stacked: 4 };

const GRAPH_COLOR_SCHEMES = {
    Accent: "accent",
    Advance: "category20c",
    Basic: "category20",
    Blue: "blues",
    Dark: "dark2",
    Green: "greens",
    Grey: "greys",
    Orange: "oranges",
    Pastel: "pastel1",
    Purple: "purples",
    Red: "reds",
    Set: "set1",
    Tableau: "tableau20"
};

const GRAPH_SINGLE_COLORS = {
    Blue: "blue",
    Cyan: "cyan",
    Green: "green",
    Indigo: "indigo",
    Orange: "orange",
    Purple: "purple",
    Red: "red",
    Teal: "teal",
    Yellow: "yellow"
};

const AXIS_BIN_TYPES = {
    NumberBin: 1,
    SizeBin: 2,
    DayOfTheWeek: 3,
    DayOfMonth: 4,
    Month: 5,
    MonthYear: 6,
    Year: 7
};

const AXIS_PROPERTY_TYPE = {
    AxisType: "axistype",
    Configuration: "configuration",
    Label: "label",
    Aggregate: "aggregate",
    Color: "color"
};

const SELECTED_GRID_ITEM_PROPERTY = "SelectedGridItem";

export {
    GRAPH_TYPE,
    GRAPH_COLOR_SCHEMES,
    GRAPH_SINGLE_COLORS,
    EDIT_GRAPH_CATEGORY_FUNCTIONS,
    AXIS_BIN_TYPES,
    AXIS_PROPERTY_TYPE,
    SELECTED_GRID_ITEM_PROPERTY
};

const AXIS_TYPE = { Column: 1, Row: 2, Stacked: 3, Filter: 4 };
const AGGREGRATION_TYPE = { Count: "Count", Sum: "Sum", Average: "Average" };

export { AXIS_TYPE, AGGREGRATION_TYPE };

export function getDirection(event, element, margin) {
    const boundBox = element.getBoundingClientRect();
    const minWidth = boundBox.right - margin[0];
    const minHeight = boundBox.bottom - margin[1];

    const rightSide = boundBox.right >= event.clientX && event.clientX > minWidth;
    const bottomSide = boundBox.bottom >= event.clientY && event.clientY > minHeight;

    if (bottomSide && rightSide) {
        return DIRECTIONAL_AXIS.diagonal;
    } else if (rightSide) {
        return DIRECTIONAL_AXIS.horizontal;
    } else if (bottomSide) {
        return DIRECTIONAL_AXIS.vertical;
    } else {
        return DIRECTIONAL_AXIS.none;
    }
}

export function getWidgetSubOptions(widgetType) {
    switch (widgetType) {
        case WIDGET_TYPE.graph:
            return GRAPH_TYPE;
        case WIDGET_TYPE.table:
        case WIDGET_TYPE.none:
            return {};
    }
}

export function indexOfFirst(arr, prop, value) {
    for (let i = 0; i < arr.length; i++) {
        if (arr[i][prop] == value) {
            return i;
        }
    }
    return -1;
}

let CREATED_AXIS_COUNT = 0;

export function createEmptyAxis(graphId, axisType, graphType, dataTypeId) {
    const result = {
        DataFieldId: null,
        AxisProperties: {},
        GraphId: graphId,
        IsNew: true,
        PublicId: CREATED_AXIS_COUNT
    };
    result.AxisProperties["axistype"] = axisType;
    result.AxisProperties["label"] = "";

    if (axisType == "Column") {
        switch (graphType) {
            case GRAPH_TYPE.bar:
                result.AxisProperties["color"] = GRAPH_SINGLE_COLORS.Blue;
                break;
            case GRAPH_TYPE.pie:
            case GRAPH_TYPE.stacked:
                result.AxisProperties["color"] = GRAPH_COLOR_SCHEMES.Basic;
                break;
        }
    }

    if (axisType == "Filter") {
        const filters = [];
        let defaultFilterOperand = "";
        if (dataTypeId == DATA_FIELD_TYPE.Boolean) {
            defaultFilterOperand = false;
        }
        if (dataTypeId == DATA_FIELD_TYPE.Choice) {
            defaultFilterOperand = "[]";
        }
        filters.push({ filterOperator: FILTER_OPERATOR.equals, filterOperand: defaultFilterOperand, filterType: 1 });

        const completeFilter = { filters, groupOperator: EXPRESSION_TYPES.And };

        result.AxisProperties["filter"] = JSON.stringify(completeFilter);
    }

    // Increment CREATED_AXIS_COUNT in order to keep public ids unique
    CREATED_AXIS_COUNT++;

    return result;
}

export function getDefaultAxesByGraphType(graphId, graphTypeId) {
    const axes = [];
    switch (graphTypeId) {
        case GRAPH_TYPE.stacked:
            axes.push(createEmptyAxis(graphId, "Stacked", graphTypeId));
            axes.push(createEmptyAxis(graphId, "Column", graphTypeId));
            break;
        case GRAPH_TYPE.bar:
        case GRAPH_TYPE.pie:
            axes.push(createEmptyAxis(graphId, "Column", graphTypeId));
            break;
        default:
            break;
    }

    // Default to Project Count
    const row = createEmptyAxis(graphId, "Row", graphTypeId);
    axes.push(row);

    return axes;
}

export function getEmptyGraphBag(payloadId, graphType) {
    const graphPayload = { Graph: null, Axes: null };
    graphPayload.Graph = {
        Title: "",
        TypeId: graphType,
        PayloadId: payloadId,
        IsNew: true
    };
    graphPayload.Axes = getDefaultAxesByGraphType(graphPayload.Graph.PayloadId, graphType);
    return graphPayload;
}

export function getAxisPropertyValue(axisProperties, propertyName) {
    return axisProperties[propertyName];
}

export function addOrUpdateAxisProperty(axisProperties, propertyName, propertyValue) {
    let found = false;
    axisProperties.forEach(function (axisProperty) {
        if (axisProperty.PropertyName == propertyName) {
            axisProperty.PropertyValue = propertyValue;
            found = true;
        }
    });
    if (!found) {
        axisProperties.push({ PropertyName: propertyName, PropertyValue: propertyValue });
    }
}

export function copyAxisProperties(originalProperties) {
    const newProperties = Object.assign({}, originalProperties);
    return newProperties;
}

export function calculatePosition(startingPos, contentBox, boundingBox, offSetX, offSetY) {
    const farthestEdgeX = startingPos.x + contentBox.offsetWidth + offSetX;
    let x = 0;
    // Check if content box is within bounding box
    if (farthestEdgeX > boundingBox.width) {
        x = startingPos.x - contentBox.offsetWidth - offSetX;
    } else {
        x = startingPos.x + offSetX;
    }

    const farthestEdgeY = startingPos.y + contentBox.offsetHeight + offSetY;
    let y = 0;
    if (farthestEdgeY > boundingBox.height) {
        y = startingPos.y - contentBox.offsetHeight - offSetY;
    } else {
        y = startingPos.y + offSetY;
    }

    return { x: x, y: y };
}

export function SearchContent(field, contentType) {
    this.Name = field.Name;
    this.PublicId = field.PublicId;
    this.TypeId = field.TypeId;
    this.ListId = field.ListId;
    this.DataFieldGroupId = field.DataFieldGroupId;
    this.EncodingTypeId = field.EncodingTypeId;
    this.IsMultiInstance = field.IsMultiInstance;
    this.IsArchived = field.IsArchived;
    this.ChildSearchContent = null;
    this.ContentType = contentType;
}

SearchContent.prototype.IsGroupParent = function () {
    return this.ContentType == CONTENT_TYPE.DataFieldGroup;
};

export function getOffSetRect($el) {
    const rect = $el.getBoundingClientRect();
    const rootElement = document.documentElement;
    const offset = {
        top: rect.top + window.pageYOffset - rootElement.clientTop,
        left: rect.left + window.pageXOffset - rootElement.clientLeft
    };
    return offset;
}

// Checks to see if an axis needs to be configured by the user.
export function axisUseConfiguration(axis) {
    return (
        axis.BuiltInFieldId == BUILT_IN_FIELD_TYPE.TotalTimeBetweenStatuses ||
        axis.BuiltInFieldId == BUILT_IN_FIELD_TYPE.SpecificStatusTimestamp
    );
}

export function getAxisUniqueName(axis) {
    const prefix = axis.AxisProperties["axistype"];
    const idValue = axis.DataFieldId ? axis.DataFieldId : axis.BuiltInFieldId;
    return `${prefix}${idValue}`;
}

export function getAxisId(axis) {
    const prefix = axis.AxisProperties["axistype"];
    const idValue = axis.DataFieldId ? axis.DataFieldId : axis.BuiltInFieldId;
    return `${prefix}${idValue}`;
}

function calculateGridItemHeight(gridItem, unitDimensions, margin) {
    return gridItem.h * unitDimensions.height + margin.height * (gridItem.h - 1);
}

function calculateGridItemWidth(gridItem, unitDimensions, margin) {
    return gridItem.w * unitDimensions.width + margin.width * (gridItem.w - 1);
}

export function calculateGridItemDimensions(gridItem, unitDimensions, margin) {
    const height = calculateGridItemHeight(gridItem, unitDimensions, margin);
    const width = calculateGridItemWidth(gridItem, unitDimensions, margin);
    return {
        height: height,
        width: width
    };
}
